<template>
  <div class="relative flex w-fit flex-row items-center gap-[10px]">
    <input
      :id="name"
      :ref="el => (refs[name] = el)"
      v-model="vModel"
      :checked.attr="vModel"
      type="checkbox"
      :name="name"
      :value="value"
      :tabindex="focusable && !disabled ? 0 : -1"
      :disabled="disabled"
      :indeterminate="indeterminate"
      class="relative float-left size-5 min-w-[20px] appearance-none border-[1.6px] border-solid bg-white outline-none transition-all duration-200 checked:border-none checked:bg-black-100 checked:after:absolute checked:after:inset-x-0 checked:after:m-auto checked:after:mt-[2px] checked:after:block checked:after:h-[0.8125rem] checked:after:w-1.5 checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] indeterminate:border-none indeterminate:bg-black-100 indeterminate:after:absolute indeterminate:after:inset-x-0 indeterminate:after:mx-auto indeterminate:after:mt-[9px] indeterminate:after:w-[12px] indeterminate:after:rounded-md indeterminate:after:border-t-[1.6px] indeterminate:after:border-solid indeterminate:after:border-white hover:border-black-120 checked:hover:bg-black-100 indeterminate:hover:bg-black-100 focus-visible:border-black-60 focus-visible:ring-[1.6px] focus-visible:ring-success-100 focus-visible:after:absolute focus-visible:after:z-[1] focus-visible:after:block focus-visible:after:size-3.5 focus-visible:after:rounded-sm focus-visible:after:content-[''] checked:focus-visible:bg-black-60 checked:focus-visible:after:inset-x-0 checked:focus-visible:after:m-auto checked:focus-visible:after:mt-[2px] checked:focus-visible:after:h-[0.8125rem] checked:focus-visible:after:w-1.5 checked:focus-visible:after:rotate-45 checked:focus-visible:after:rounded-none checked:focus-visible:after:border-[0.125rem] checked:focus-visible:after:border-l-0 checked:focus-visible:after:border-t-0 checked:focus-visible:after:border-solid checked:focus-visible:after:border-white checked:focus-visible:after:bg-transparent indeterminate:focus-visible:border-none indeterminate:focus-visible:bg-black-60 indeterminate:focus-visible:after:absolute indeterminate:focus-visible:after:inset-x-0 indeterminate:focus-visible:after:mx-auto indeterminate:focus-visible:after:mt-[9px] indeterminate:focus-visible:after:h-[1.6px] indeterminate:focus-visible:after:w-[12px] indeterminate:focus-visible:after:rounded-md indeterminate:focus-visible:after:border-none indeterminate:focus-visible:after:bg-white active:border-black-100 active:ring-0 checked:active:bg-black-100 active:focus-visible:border-black-60 focus-visible:active:border-black-100 disabled:border-black-40 disabled:bg-black-10 checked:disabled:bg-black-10 indeterminate:disabled:bg-black-10 indeterminate:disabled:after:border-black-40"
      :class="[
        round ? 'rounded-full' : 'rounded-[4px]',
        borderColorClass,
        readOnly || disabled ? 'hover:cursor-default' : 'hover:cursor-pointer',
      ]"
      @click="
        event => {
          if (readOnly) event.preventDefault()
        }
      "
    />
    <label
      v-if="label"
      class="text-body-2 w-full"
      :class="[readOnly || disabled ? 'cursor-default' : 'cursor-pointer']"
      :for="name"
      >{{ label }}</label
    >
    <slot v-else :for="name" name="label"></slot>
    <transition name="fade">
      <div v-if="error" class="absolute -bottom-4 h-4 w-full">
        <p
          :class="[{ '!w-max': inlineError }]"
          class="text-caption-2 mx-4 flex flex-row items-center justify-start text-error-100"
        >
          {{ error }}
        </p>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
const emits = defineEmits(['update:modelValue'])

type Props = {
  modelValue: boolean | undefined
  name: string
  label?: string
  disabled?: boolean
  readOnly?: boolean
  indeterminate?: boolean
  focusable?: boolean
  round?: boolean
  borderColorClass?: string
  value?: any
  error?: string
  inlineError?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  focusable: true,
  borderColorClass: 'border-black-100',
  value: true,
  error: '',
})

const vModel = useVModel(props, 'modelValue', emits)
const refs = {
  [props.name]: ref<HTMLInputElement>(),
}

onMounted(() => {
  watch(
    () => props.indeterminate,
    value => {
      if (refs[props.name]) {
        refs[props.name].indeterminate = value
      }
    },
    { immediate: true },
  )
})
</script>

<style scoped></style>
